/**
 * states.js —— 状态机与时序
 *
 * 这是「视觉不是贴在算法外面的皮」的落点：
 * 摇卦阶段每握一次拳掷一爻，第 n 层环随之点亮定住，
 * 掷出老阳老阴时该层转赤并持续脉动。六层点满，成卦。
 *
 * 动画不引 GSAP，自己跑一条极简的 tween 队列——
 * 依赖越少，Codex 后面调参数的自由度越大。
 */

import {
  CAST_RITUAL, CELL_HIGHLIGHT, GESTURE, RING_STYLE, SPIN, TIMING
} from './tuning.js';
import { coinToss, systemRandom } from '../src/cast.js';
import { fourPillars } from '../src/ganzhi.js';
import { analyze } from '../src/analyze.js';
import { scoreTimingCandidates } from '../src/timing.js';

export const STATE = {
  DORMANT: 'dormant',     // 静默：只有星场
  AIM: 'aim',             // 立意：伸食指，阵眼随指尖
  EXPANDING: 'expanding', // 张掌：十二层由阵眼逐层向外展开
  CASTING: 'casting',     // 摇卦：握拳六次
  FORMED: 'formed',       // 成卦：立体阵展开
  TRANSFORM: 'transform', // 变卦：张掌，旋涡与金环
  COLLAPSE: 'collapse'    // 收卦
};

/** 食指跟随只属于唤醒态；摇卦开始后阵眼保持在最后位置。 */
export const isAimFollowState = (state) => state === STATE.AIM;

/** 掌心控向只在立体成卦与旋涡态生效。 */
export const isDepthOrientationState = (state) => (
  state === STATE.FORMED || state === STATE.TRANSFORM
);

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeIn = (t) => t * t * t;
const MS_PER_SECOND = 1000;
const expansionEase = (t) => {
  const peakAt = 0.78;
  const startScale = RING_STYLE.revealScaleMin;
  const overshootProgress = (TIMING.expandOvershoot - startScale) / (1 - startScale);
  if (t <= peakAt) return easeOut(t / peakAt) * overshootProgress;
  return overshootProgress
    + (1 - overshootProgress) * easeInOut((t - peakAt) / (1 - peakAt));
};

/** 极简 tween：对 target 上的一个数值属性做插值 */
class Tween {
  constructor(get, set, to, duration, ease, delay = 0, onDone) {
    this.get = get; this.set = set; this.to = to;
    this.duration = duration; this.ease = ease;
    this.delay = delay; this.onDone = onDone;
    this.elapsed = 0; this.from = null; this.done = false;
  }
  update(dt) {
    if (this.done) return;
    this.elapsed += dt;
    if (this.elapsed < this.delay) return;
    if (this.from === null) this.from = this.get();
    const t = Math.min(1, (this.elapsed - this.delay) / this.duration);
    this.set(this.from + (this.to - this.from) * this.ease(t));
    if (t >= 1) { this.done = true; this.onDone?.(); }
  }
}

export class Director {
  /**
   * @param {Field} field
   * @param {object} o
   *   onEvent    (name, payload) => void  用于驱动 HUD
   *   rand       随机源，测试时可注入种子
   *   category   问事分类，决定用神
   */
  constructor(field, o = {}) {
    this.field = field;
    this.onEvent = o.onEvent || (() => {});
    this.rand = o.rand || systemRandom();
    this.category = o.category || null;
    this.question = o.question || '';
    this.timeScope = ['near', 'mid', 'long'].includes(o.timeScope) ? o.timeScope : 'none';

    this.state = STATE.DORMANT;
    this.tweens = [];
    this.values = [];      // 已摇出的爻值 6/7/8/9
    this.result = null;    // analyze() 的结果
    this.timing = null;    // 应期候选；只用于可视化，不参与状态判断
    this._castAt = null;   // 起卦时刻，第一爻落下时锁定
    this._cooldown = 0;
    this.mode = 'liuyao';
  }

  /** 类别只允许在尚未成卦时调整；成卦后 result 存在即锁定。 */
  setCategory(category = null) {
    if (this.result) return false;
    this.category = typeof category === 'string' && category.trim() ? category.trim() : null;
    return true;
  }

  /** 具体问题与类别一样，在成卦后锁定，避免报告上下文中途变化。 */
  setQuestion(question = '') {
    if (this.result) return false;
    this.question = typeof question === 'string' ? question.trim().slice(0, 120) : '';
    return true;
  }

  /** 时间范围在成卦后同样锁定，防止应期上下文中途改变。 */
  setTimeScope(timeScope = 'none') {
    if (this.result) return false;
    this.timeScope = ['near', 'mid', 'long'].includes(timeScope) ? timeScope : 'none';
    return true;
  }

  /* ------------------------------------------------- 内部工具 */

  _tween(key, to, duration, ease = easeOut, delay = 0, onDone) {
    const c = this.field.control;
    const queuedAt = performance.now();
    const tween = new Tween(
      () => c[key],
      (v) => { c[key] = v; },
      to,
      duration,
      ease,
      delay,
      () => {
        const actual = (performance.now() - queuedAt) / MS_PER_SECOND - delay;
        console.info('[scene:tween]', JSON.stringify({
          key,
          targetDuration: duration,
          actualDuration: Math.max(0, actual),
          delay
        }));
        onDone?.();
      }
    );
    tween.key = key;
    this.tweens.push(tween);
  }

  _tweenLayer(i, to, duration, ease = easeOut, delay = 0, onDone) {
    const arr = this.field.control.layerReveal;
    const key = `layerReveal[${i}]`;
    const queuedAt = performance.now();
    const tween = new Tween(
      () => arr[i],
      (v) => { arr[i] = v; },
      to,
      duration,
      ease,
      delay,
      () => {
        const actual = (performance.now() - queuedAt) / MS_PER_SECOND - delay;
        console.info('[scene:tween]', JSON.stringify({
          key,
          targetDuration: duration,
          actualDuration: Math.max(0, actual),
          delay
        }));
        onDone?.();
      }
    );
    tween.key = key;
    this.tweens.push(tween);
  }

  _tweenHighlightLayer(i, to, duration, ease = easeOut, delay = 0, onDone) {
    const arr = this.field.control.highlightReveal;
    const key = `highlightReveal[${i}]`;
    const queuedAt = performance.now();
    const tween = new Tween(
      () => arr[i],
      (v) => { arr[i] = v; },
      to,
      duration,
      ease,
      delay,
      () => {
        const actual = (performance.now() - queuedAt) / MS_PER_SECOND - delay;
        console.info('[scene:tween]', JSON.stringify({
          key,
          targetDuration: duration,
          actualDuration: Math.max(0, actual),
          delay
        }));
        onDone?.();
      }
    );
    tween.key = key;
    this.tweens.push(tween);
  }

  _tweenLayerScale(i, to, duration, ease = easeOut, delay = 0, onDone) {
    const arr = this.field.control.layerScale;
    const key = `layerScale[${i}]`;
    const tween = new Tween(
      () => arr[i],
      (v) => { arr[i] = v; },
      to,
      duration,
      ease,
      delay,
      onDone
    );
    tween.key = key;
    this.tweens.push(tween);
  }

  _clearTweens() { this.tweens.length = 0; }
  _cancelTween(key) { this.tweens = this.tweens.filter((tween) => tween.key !== key); }

  _delay(key, duration, onDone) {
    const tween = new Tween(() => 0, () => {}, 1, 0.001, easeOut, duration, onDone);
    tween.key = key;
    this.tweens.push(tween);
  }

  _setCalibrationStatus(active) {
    this.onEvent('orientation-calibrated', { active, duration: TIMING.formedHoldDuration });
  }

  _flashTaiji() {
    const half = CAST_RITUAL.completionDuration / 2;
    this._cancelTween('taijiFlash');
    this._tween('taijiFlash', 1, half, easeOut, 0, () => {
      this._tween('taijiFlash', 0, half, easeIn);
    });
  }

  _beginFormedHold() {
    this.field.control.spinMultiplier = SPIN.formedHold;
    this._setCalibrationStatus(true);
    this._cancelTween('formedHold');
    this._delay('formedHold', TIMING.formedHoldDuration, () => {
      if (this.state !== STATE.FORMED) return;
      this.field.resumeIdleSpin(false);
      this.field.control.spinMultiplier = SPIN.formed;
      this._setCalibrationStatus(false);
    });
  }

  /* --------------------------------------------------- 状态迁移 */

  toDormant() {
    this.state = STATE.DORMANT;
    this._clearTweens();
    this._setCalibrationStatus(false);
    this.field.finishCastWave();
    this.field.resumeIdleSpin(true);
    this.field.setOrientation({});
    this.field.setAimPosition({});
    this._tween('expand', 0, TIMING.toPlane, easeIn);
    this._tween('depth', 0, TIMING.toPlane, easeIn);
    this._tween('vortex', 0, TIMING.toPlane * 0.6, easeIn);
    this._tween('awaken', 0, TIMING.awaken, easeIn);
    this._tween('completionPulse', 0, TIMING.toPlane * 0.5, easeIn);
    this._tween('taijiFlash', 0, TIMING.toPlane * 0.5, easeIn);
    this._tween('ringBaseOpacity', 0, TIMING.toPlane, easeIn);
    this.field.control.spinMultiplier = 0;
    this.field.control.layerReveal.forEach((_, i) => this._tweenLayer(i, 0, TIMING.toPlane, easeIn));
    this.field.control.layerScale.forEach((_, i) =>
      this._tweenLayerScale(i, RING_STYLE.revealScaleMin, TIMING.toPlane, easeIn)
    );
    this.field.control.highlightReveal.forEach((_, i) =>
      this._tweenHighlightLayer(i, 0, TIMING.toPlane, easeIn)
    );
    this.field.highlightCells({});
    this.field.setMovingLines([]);
    this.values = [];
    this.result = null;
    this.timing = null;
    this._castAt = null;
    this.onEvent('state', { state: this.state });
  }

  /** UI 模式切换：梅花只展开内圈，切回六爻则回到静默等待起卦。 */
  switchMode(mode = 'liuyao') {
    this.mode = mode === 'meihua' ? 'meihua' : 'liuyao';
    this.state = STATE.DORMANT;
    this._clearTweens();
    this._setCalibrationStatus(false);
    this.values = [];
    this.result = null;
    this.timing = null;
    this._castAt = null;
    this._cooldown = 0;
    this.field.setMode(this.mode);
    this.field.setOrientation({});
    this.field.setAimPosition({});
    this.field.highlightCells({});
    this.field.setMovingLines([]);
    this.field.finishCastWave();
    this.field.resumeIdleSpin(true);
    const expanded = this.mode === 'meihua' ? 1 : 0;
    Object.assign(this.field.control, {
      awaken: expanded,
      expand: expanded,
      depth: 0,
      vortex: 0,
      shock: 0,
      completionPulse: 0,
      taijiFlash: 0,
      ringSnap: 0,
      ringBaseOpacity: expanded,
      spinMultiplier: expanded ? SPIN.aim : 0
    });
    this.field.control.layerReveal.fill(expanded);
    this.field.control.layerScale.fill(expanded ? 1 : RING_STYLE.revealScaleMin);
    this.field.control.highlightReveal.fill(0);
    this.onEvent('state', { state: this.state, mode: this.mode });
  }

  toAim() {
    if (this.state !== STATE.DORMANT) return;
    if (this.state === STATE.DORMANT) {
      this._clearTweens();
      this.field.control.layerReveal.fill(0);
      this.field.control.layerScale.fill(RING_STYLE.revealScaleMin);
      this.field.control.highlightReveal.fill(0);
    }
    this.state = STATE.AIM;
    // AIM 只有阵眼；十二层保持 opacity=0，并在 0.12 尺寸等待张掌展开。
    this.field.setOrientation({});
    this.field.resumeIdleSpin(false);
    this.field.control.spinMultiplier = 0;
    this.field.control.expand = 0;
    this.field.control.ringBaseOpacity = 0;
    this._tween('awaken', 1, TIMING.awaken, easeOut);
    this.onEvent('state', { state: this.state, hint: ['凝神定意', 'HOLD YOUR QUESTION'] });
  }

  /** 张掌后由内向外展开完整罗盘；最后一层落稳后自动进入摇卦态。 */
  toExpanding() {
    if (this.state !== STATE.AIM) return false;
    this.state = STATE.EXPANDING;
    this._clearTweens();
    // AIM 允许阵眼跟随指尖平移；进入完整罗盘阶段必须回到视口中心，
    // 否则最后一次指尖位置会残留到 CASTING / FORMED，造成成卦整体偏下或偏侧。
    this.field.setAimPosition({});
    this.field.control.spinMultiplier = 0;
    this.field.control.ringBaseOpacity = 0;
    this.field.control.layerReveal.fill(0);
    this.field.control.layerScale.fill(RING_STYLE.revealScaleMin);
    this._tween('expand', 1, TIMING.expandDuration, easeOut);

    const layerCount = this.field.control.layerReveal.length;
    const layerDuration = TIMING.expandDuration - (layerCount - 1) * TIMING.expandStagger;
    this.field.control.layerReveal.forEach((_, i) => {
      const delay = i * TIMING.expandStagger;
      this._tweenLayer(i, 1, layerDuration, easeOut, delay);
      this._tweenLayerScale(i, 1, layerDuration, expansionEase, delay, () => {
        if (i !== layerCount - 1 || this.state !== STATE.EXPANDING) return;
        this.field.control.layerReveal.fill(1);
        this.field.control.layerScale.fill(1);
        this.field.control.spinMultiplier = SPIN.casting;
        this.state = STATE.CASTING;
        this._flashTaiji();
        this.onEvent('state', { state: this.state });
      });
    });
    this.onEvent('state', { state: this.state });
    return true;
  }

  /**
   * 摇一爻。每次握拳调用一次，第六次自动成卦。
   * force 仅给键盘入口使用，跳过专属于手势的 cooldown。
   * 返回本次掷出的爻值，若在冷却中（且未 force）或已满六爻则返回 null。
   */
  castLine(force = false) {
    if (this.state !== STATE.CASTING) return null;
    if (!force && this._cooldown > 0) return null;
    if (this.values.length >= 6) return null;
    if (!this._castAt) {
      // 起卦时刻在第一爻落下时锁定，之后摇多久都不影响月建日辰
      this._castAt = nowInBeijing();
    }

    const toss = coinToss(this.rand);
    const i = this.values.length;
    this.values.push(toss.value);
    this._cooldown = GESTURE.cooldown;

    const isMoving = toss.value === 6 || toss.value === 9;
    if (isMoving) {
      const movingPositions = this.values
        .map((value, index) => (value === 6 || value === 9 ? index + 1 : null))
        .filter(Boolean);
      this.field.setMovingLines(movingPositions);
    }

    // 罗盘已完整展开；第 n 爻只点亮对应层、快速自转、急停并定格变色。
    const layerIndex = this.field.beginCastLayer(i + 1);
    this._delay(`castLayer[${layerIndex}]`, TIMING.castSpin, () => {
      this.field.settleCastLayer(layerIndex, isMoving);
      this._cancelTween('castWave');
      this._tween('castWave', 1, CAST_RITUAL.waveDuration, easeOut, 0, () => {
      this.field.finishCastWave();
      });
      this.onEvent('line-settled', { index: i + 1, layerIndex, moving: isMoving });
      if (i === 5) {
        // Demo.1 从第六爻视觉定格后起声，卦象在片段主峰处显现。
        this.onEvent('formation-transition', { index: 6, layerIndex, moving: isMoving });
      }
    });
    this.onEvent('line', {
      index: i + 1,
      value: toss.value,
      coins: toss.coins,
      moving: isMoving,
      remaining: 6 - this.values.length
    });

    if (this.values.length === 6) {
      this.tweens.push(
        new Tween(
          () => 0,
          () => {},
          1,
          0.01,
          easeOut,
          TIMING.castSpin + Math.max(TIMING.settle, TIMING.formationSoundLead),
          () => this.form()
        )
      );
    }
    return toss.value;
  }

  /** 成卦：排盘 + 立体展开 */
  form() {
    if (!this._castAt) this._castAt = nowInBeijing();
    // 防御性归中：键盘、测试或未来入口即使跳过标准展开路径，成卦也必须居中。
    this.field.setAimPosition({});
    const pillars = fourPillars(this._castAt);
    this.result = analyze({
      values: this.values,
      pillars,
      question: this.question,
      category: this.category,
      timeHorizon: this.timeScope === 'near' ? 'near'
        : ['mid', 'long'].includes(this.timeScope) ? 'far' : null,
      timeScope: this.timeScope
    });
    this.timing = scoreTimingCandidates(this.result);

    const highlightedLayers = this.field.highlightCells(cellMapFromResult(this.result, this.timing));
    this.field.control.highlightReveal.fill(0);

    this.state = STATE.FORMED;
    this.field.control.spinMultiplier = SPIN.formedHold;
    this.field.beginSnapToPosition();
    this._tween('ringSnap', 1, TIMING.snapToPosition, easeInOut, 0, () => {
      this._beginFormedHold();
    });
    const completionDelay = 0;
    const halfPulse = CAST_RITUAL.completionDuration / 2;
    this._tween('completionPulse', 1, halfPulse, easeOut, completionDelay, () => {
      this._tween('completionPulse', 0, halfPulse, easeIn);
    });
    this._tween(
      'depth',
      1,
      TIMING.toDepth,
      easeInOut,
      0
    );
    highlightedLayers.forEach((layerIndex) =>
      this._tweenHighlightLayer(
        layerIndex,
        1,
        CELL_HIGHLIGHT.revealDuration,
        easeOut,
        layerIndex * CELL_HIGHLIGHT.layerStagger
      )
    );
    this.onEvent('formed', { result: this.result, timing: this.timing });
    return this.result;
  }

  /** 变卦：张掌，动爻翻转，金环亮起 */
  toTransform() {
    if (this.state !== STATE.FORMED || !this.result) return;
    this._cancelTween('formedHold');
    this._setCalibrationStatus(false);
    this.field.resumeIdleSpin(false);
    this.field.control.spinMultiplier = SPIN.vortex;
    this.state = STATE.TRANSFORM;
    // 首次变卦时 depth 已为 1；收卦回平面后再次张掌时 depth 为 0。
    // 两条路径统一在这里恢复立体深度，避免只亮金环却仍停留在平面。
    this._tween('depth', 1, TIMING.toDepth, easeInOut);
    this._tween('vortex', 1, TIMING.vortexOn, easeOut);
    // 一次性冲击波
    this._tween('shock', 1, TIMING.vortexOn * 0.4, easeOut, 0, () => {
      this._tween('shock', 0, TIMING.vortexOn * 1.2, easeIn);
    });
    this.onEvent('transform', {
      result: this.result,
      primary: this.result.primary,
      changed: this.result.changed,
      moving: this.result.moving
    });
  }

  /** 收卦回平面 */
  toCollapse() {
    this.state = STATE.COLLAPSE;
    this.onEvent('state', { state: this.state });
    this.field.resumeIdleSpin(false);
    this.field.control.spinMultiplier = SPIN.formed;
    this._tween('vortex', 0, TIMING.toPlane, easeIn);
    this._tween('depth', 0, TIMING.toPlane, easeIn, 0, () => {
      this.state = STATE.FORMED;
      this.field.control.spinMultiplier = SPIN.formed;
      this.onEvent('state', { state: this.state, collapsed: true });
    });
    this.onEvent('collapse', {});
  }

  /* ------------------------------------------------------- 帧更新 */

  update(dt) {
    if (this._cooldown > 0) this._cooldown = Math.max(0, this._cooldown - dt);
    for (const t of this.tweens) t.update(dt);
    if (this.tweens.length > 24) this.tweens = this.tweens.filter((t) => !t.done);
    this.field.update(dt);
  }

  /**
   * 手势事件入口。gesture 为 'fist' | 'palm' | 'point' | null
   * 只在手势**变化**时调用，不要每帧调。
   */
  onGesture(gesture) {
    switch (gesture) {
      case 'point':
        if (this.state === STATE.DORMANT) this.toAim();
        break;
      case 'fist':
        if (this.state === STATE.CASTING) this.castLine();
        else if (this.state === STATE.TRANSFORM) this.toCollapse();
        break;
      case 'palm':
        if (this.state === STATE.AIM) this.toExpanding();
        else if (this.state === STATE.FORMED) this.toTransform();
        break;
      default:
        break;
    }
  }
}

/** 只把 analyze() 已有结果映射到罗盘格，不新增任何易学判断。 */
export function cellMapFromResult(result, timing = null) {
  const moving = new Set(result.moving || []);
  const usePosition = result.useGod?.primaryEvidence?.position;
  const useBranch = result.useGod?.primaryEvidence?.branch;
  const useAbsent = Boolean(result.useGod?.isAbsent);
  const itemPriority = { normal: 0, useGod: 1, moving: 2 };
  const lineItems = (field) => {
    const byToken = new Map();
    result.yao.forEach((line) => {
      const item = {
        token: line[field],
        kind: moving.has(line.position)
          ? 'moving'
          : (!useAbsent && line.position === usePosition ? 'useGod' : 'normal'),
        pulse: moving.has(line.position)
      };
      const previous = byToken.get(item.token);
      if (!previous || itemPriority[item.kind] > itemPriority[previous.kind]) {
        byToken.set(item.token, item);
      }
    });
    if (field === 'branch' && useAbsent && useBranch) {
      byToken.set(useBranch, { token: useBranch, kind: 'useGod', pulse: false });
    }
    return [...byToken.values()];
  };
  const voidItems = (result.pillars.旬空 || []).map((branch) => ({
    token: branch,
    kind: 'void',
    dashed: true
  }));
  const changedName = result.changed?.name;
  const shiLine = result.yao.find(({ isShi }) => isShi);
  const hexagramItems = [
    { token: result.primary.name, kind: 'primary' },
    ...(changedName ? [{ token: changedName, kind: 'changed' }] : [])
  ];

  const connectionMap = new Map();
  const addConnection = (from, to, kind) => {
    if (!from || !to || from === to) return;
    connectionMap.set(`${from}:${to}:${kind}`, { from, to, kind });
  };
  if (useBranch && shiLine) addConnection(useBranch, shiLine.branch, 'useGodToShi');
  for (const line of result.useGod?.generatedBy || []) {
    addConnection(line.branch, useBranch, 'generate');
  }
  for (const line of result.useGod?.overcomeBy || []) {
    addConnection(line.branch, useBranch, 'overcome');
  }
  for (const line of result.yao || []) {
    if (line.moving && line.transform?.toBranch) {
      addConnection(line.branch, line.transform.toBranch, 'transform');
    }
  }

  const timingByGanzhi = new Map();
  for (const candidate of timing?.candidates || []) {
    const grouped = timingByGanzhi.get(candidate.ganzhi) || [];
    grouped.push(candidate);
    timingByGanzhi.set(candidate.ganzhi, grouped);
  }
  const timingItems = [...timingByGanzhi.entries()].map(([token, candidates]) => {
    const confidence = Math.max(...candidates.map((candidate) => candidate.confidence));
    const boost = CELL_HIGHLIGHT.timingMinBoost
      + confidence * (CELL_HIGHLIGHT.timingMaxBoost - CELL_HIGHLIGHT.timingMinBoost);
    const dateText = ({ date }) => (
      `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
    );
    const lines = candidates.flatMap((candidate) => candidate.rules.map((rule) => (
      `${dateText(candidate)} · ${rule.name} · ${rule.source.book}·${rule.source.chapter}`
    )));
    return {
      token,
      kind: 'timing',
      confidence,
      boost,
      tooltip: {
        title: `${token} · 规则置信度 ${Math.round(confidence * 100)}%`,
        lines: [...new Set(lines)],
        note: timing.note
      }
    };
  });

  return {
    trigrams: {
      items: [...new Set([result.primary.lower, result.primary.upper])]
        .map((token) => ({ token, kind: 'primary' }))
    },
    stems: { items: lineItems('stem') },
    branches: {
      items: [...lineItems('branch'), ...voidItems],
      connections: [...connectionMap.values()]
    },
    terms: { items: [{ token: result.pillars.节, kind: 'term' }] },
    ganzhi: {
      items: [
        { token: result.pillars.月, kind: 'month' },
        { token: result.pillars.日, kind: 'day' },
        ...timingItems
      ]
    },
    hexagrams: {
      items: hexagramItems,
      connections: changedName
        ? [{ from: result.primary.name, to: changedName, kind: 'changed' }]
        : []
    }
  };
}

/** 梅花盘只点亮体、用两卦的八卦名与卦符；不混入六爻纳甲环。 */
export function cellMapFromMeihua(result) {
  const byTrigram = new Map();
  for (const trigram of [result.upper, result.lower]) {
    const kind = trigram === result.yong ? 'yong' : trigram === result.ti ? 'ti' : 'primary';
    byTrigram.set(trigram, { token: trigram, kind });
  }
  const items = [...byTrigram.values()];
  return {
    trigramSymbols: {
      items
    },
    trigrams: { items }
  };
}

/** 起卦时刻一律用北京时间，与 fourPillars 的默认时区一致 */
function nowInBeijing() {
  const now = new Date();
  const local = new Date(now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60000);
  return {
    year: local.getFullYear(),
    month: local.getMonth() + 1,
    day: local.getDate(),
    hour: local.getHours(),
    minute: local.getMinutes()
  };
}
